import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidationGroup, withCustomValidation } from '@bingads-webui-react/validation';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  Subtitle1,
  Text,
  Title3,
} from '@fluentui/react-components';
import { errorMessages } from '../errors';
import { stepLayoutTokens } from '../page-layout';
import { validateVisitConversionLift } from '../validate';
import { CountryCard } from './country/country-card';
import { StartDateCard } from './date/start-date-card';
import { ADD_KEYWORDS_BY_SELECTION } from './keyword/keyword-methods';
import { SearchLift } from './search-lift/search-lift';
import { ApiSaveErrorNotification } from './api-save-error-notification';
import { VisitConversionLift } from './visit-conversion-lift/visit-conversion-lift';
import { ExperimentNameCard } from './experiment-name-card';
import { ImpressionThresholdMessage } from './impression-threshold-message';
import { SelectedCampaignsCard } from './selected-campaigns-card';

const titles = {
  create: _TL_('Create your lift measurement experiment'),
  edit: _TL_('Edit your lift measurement experiment'),
};

const ConfigureStudyStepInternal = ({ updateValidation }) => {
  const i18n = useContext(I18nContext);
  const { state: wizardState, send } = useWizardService();
  const { wizardData } = wizardState.context;
  const { showError } = wizardData;
  const [hasLiftTypeRequiredError, setHasLiftTypeRequiredError] = useState(false);
  const selectedCampaigns = useMemo(() => (
    wizardData.selectedCampaignsById
      ? Object.values(wizardData.selectedCampaignsById).map(campaign => ({ name: campaign.Name }))
      : []
  ), [wizardData.selectedCampaignsById]);

  // Localized strings.
  const title = i18n.getString(titles[wizardData.intent]);
  const instructions = i18n.getString(_TL_('Lift measurement experiments provide reports on the branch search rate, website visitation rate, and conversion rate for your Audience campaigns (Display, Native, Online Video, and Feed-based). These measurements help you understand how much more likely consumers are to search for your brand, visit your website, and make purchases after seeing your Audience ads.'));
  const measurementSectionHeader = i18n.getString(_TL_('Measurement types'));

  const onChangeExperimentName = useCallback((event) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { experimentName: event.target.value });
  }, [send]);

  const onChangeStartDate = useCallback((startDate) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { startDate });
  }, [send]);

  const onChangeCountry = useCallback((country) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { country });
  }, [send]);

  const onToggleUseSearchLift = useCallback(() => {
    const newValue = !wizardData.useSearchLift;

    if (!newValue && !wizardData.useConversionLift && !wizardData.useVisitLift) {
      updateValidation(['MEASUREMENT_TYPE_REQUIRED']);
      setHasLiftTypeRequiredError(true);
    } else {
      setHasLiftTypeRequiredError(false);
    }

    setClarityTag({
      IsLiftExperimentUsingSearchLift: newValue,
    });
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { useSearchLift: newValue });
  }, [send, updateValidation, wizardData.useConversionLift, wizardData.useSearchLift, wizardData.useVisitLift]);

  const onChangeKeywords = useCallback((typedKeywordString) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { typedKeywordString });
  }, [send]);

  const onChangeKeywordsMethod = useCallback((method) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { keywordsMethod: method });
  }, [send]);

  const onRemoveAllKeywords = useCallback(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { selectedKeywords: [] });
  }, [send]);

  const onRemoveKeyword = useCallback((removedId) => {
    const filteredKeywords = wizardData.selectedKeywords.filter(keywordId => keywordId !== removedId);
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { selectedKeywords: filteredKeywords });
  }, [send, wizardData.selectedKeywords]);

  const onSelectKeywords = useCallback((selectedWords) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { selectedKeywords: [...selectedWords] });
  }, [send]);

  const onChangeCustomerId = useCallback((cid) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { searchCid: cid });
  }, [send]);

  const [hasUETTagsSelectionError, setHasUETTagsSelectionError] = useState(false);
  const onToggleUseVisitConversionLift = useCallback(() => {
    const isNowToggledOn = !wizardData.useVisitConversionLift;
    if (isNowToggledOn) {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { useVisitConversionLift: true });
    } else {
      setHasUETTagsSelectionError(false);
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { useVisitConversionLift: false, useVisitLift: false, useConversionLift: false });
    }
  }, [send, wizardData.useVisitConversionLift]);

  const onChangeVisitConversionLiftMethod = useCallback((values) => {
    if (!wizardData.useSearchLift && !values.useConversionLift && !values.useVisitLift) {
      updateValidation(['MEASUREMENT_TYPE_REQUIRED']);
      setHasLiftTypeRequiredError(true);
    } else {
      setHasLiftTypeRequiredError(false);
    }

    setClarityTag({
      IsLiftExperimentUsingConversionLift: values.useConversionLift,
      IsLiftExperimentUsingVisitLift: values.useVisitLift,
    });
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      useConversionLift: values.useConversionLift,
      useVisitLift: values.useVisitLift,
    });
  }, [send, updateValidation, wizardData.useSearchLift]);

  const onSelectUetTags = useCallback((selectedTags) => {
    let key;

    if (wizardData.useConversionLift && wizardData.useVisitLift) {
      key = 'both';
    } else if (wizardData.useConversionLift) {
      key = 'conversion';
    } else {
      key = 'visit';
    }
    setHasUETTagsSelectionError(false);
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      uetTags: {
        ...wizardData.uetTags,
        [key]: [...selectedTags],
      },
    });
  }, [send, wizardData]);

  const onValidate = useCallback(({ isValid }) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isValid });
  }, [send]);

  useEffect(() => {
    const isTagsSelectionValid = validateVisitConversionLift(wizardData);
    if (showError && !isTagsSelectionValid) {
      setHasUETTagsSelectionError(true);
    }
  }, [showError, wizardData]);

  return (
    <ValidationGroup validationProps={{ onValidate }}>
      <Stack tokens={stepLayoutTokens} className="configure-study-step">
        <Title3>{title}</Title3>
        <Text>
          <LearnMoreLink
            linkText={i18n.getString(_TL_('Learn more'))}
            text={instructions}
            topic="app54859"
          />
        </Text>
        {wizardData.apiSaveErrorMessage ? (
          <ApiSaveErrorNotification message={wizardData.apiSaveErrorMessage} />
        ) : (
          <ImpressionThresholdMessage />
        )}
        <SelectedCampaignsCard campaigns={selectedCampaigns} />
        <ExperimentNameCard
          experimentName={wizardData.experimentName || ''}
          onChange={onChangeExperimentName}
          showError={showError}
        />
        <StartDateCard
          startDate={wizardData.startDate || null}
          onChangeStartDate={onChangeStartDate}
          showError={showError}
        />
        <CountryCard
          country={wizardData.country || null}
          onChangeCountry={onChangeCountry}
          showError={showError}
        />
        <Subtitle1>{measurementSectionHeader}</Subtitle1>
        {showError && hasLiftTypeRequiredError && (
          <MessageBar intent="error">
            <MessageBarBody>{i18n.getString(errorMessages.MEASUREMENT_TYPE_REQUIRED)}</MessageBarBody>
          </MessageBar>
        )}
        {showError && hasUETTagsSelectionError && (
          <MessageBar intent="error">
            <MessageBarBody>{i18n.getString(errorMessages.UET_TAG_SELECTION_REQUIRED)}</MessageBarBody>
          </MessageBar>
        )}
        <SearchLift
          customerId={wizardData.searchCid || ''}
          isAllowed
          isEnabled={!!wizardData.useSearchLift}
          keywordsMethod={wizardData.keywordsMethod || ADD_KEYWORDS_BY_SELECTION}
          onChangeCustomerId={onChangeCustomerId}
          onChangeKeywords={onChangeKeywords}
          onChangeKeywordsMethod={onChangeKeywordsMethod}
          onRemoveAllKeywords={onRemoveAllKeywords}
          onRemoveKeyword={onRemoveKeyword}
          onSelectKeywords={onSelectKeywords}
          onToggle={onToggleUseSearchLift}
          selectedKeywords={wizardData.selectedKeywords || []}
          showError={showError}
          typedKeywordString={wizardData.typedKeywordString || ''}
        />
        <VisitConversionLift
          isEnabled={!!wizardData.useVisitConversionLift}
          onChangeMethod={onChangeVisitConversionLiftMethod}
          onSelectUetTags={onSelectUetTags}
          onToggle={onToggleUseVisitConversionLift}
          selectedUetTags={wizardData.uetTags}
          showError={showError}
          useConversionLift={!!wizardData.useConversionLift}
          useVisitLift={!!wizardData.useVisitLift}
        />
      </Stack>
    </ValidationGroup>
  );
};

ConfigureStudyStepInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
};

export const ConfigureStudyStep = withCustomValidation(ConfigureStudyStepInternal);
